import { FormControl, Grid, InputAdornment, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { NumberMinMax } from '@components/table/types/NumberMinMax.type';
import { DollarSign } from '@assets/svgs';
import { TextField } from '@components/textField/TextField';

export type NumberRangeFieldProps = {
  onMinChange: (value: number | null, maxNumber: number | null) => void;
  onMaxChange: (value: number | null, minNumber: number | null) => void;
  label?: string;
  disabled?: boolean;
  value?: NumberMinMax;
  defaultValue?: NumberMinMax;
};
export const NumberRangeField = ({
  onMinChange,
  onMaxChange,
  label,
  disabled,
  value,
  defaultValue,
}: NumberRangeFieldProps) => {
  const [stateValue, setStateValue] = useState({
    min: value?.min ?? defaultValue?.min ?? null,
    max: value?.max ?? defaultValue?.max ?? null,
  });

  useEffect(() => {
    setStateValue({
      min: value?.min ?? null,
      max: value?.max ?? null,
    });
  }, [value]);

  return (
    <Grid
      container
      size={{ xs: 12 }}
      sx={{
        padding: 0,
        alignItems: 'center',
        flexDirection: 'row',
        gap: '4px',
      }}
    >
      <Grid sx={{ flex: 2 }}>
        <FormControl>
          {label && (
            <Typography variant='caption' color='text.main' sx={{ mb: 0.7 }}>
              {label}
            </Typography>
          )}

          <Grid container>
            <Grid sx={{ width: 'auto' }}>
              <TextField
                type='number'
                inputMode='numeric'
                slotProps={{
                  htmlInput: {
                    max: stateValue?.max,
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment
                        position='start'
                        sx={(theme) => ({
                          height: 0,
                          svg: {
                            ...theme.icons.xs,
                            ml: '-12px',
                          },
                        })}
                      >
                        <DollarSign />
                      </InputAdornment>
                    ),
                  },
                }}
                value={stateValue ? stateValue.min : null}
                onChange={(e) => {
                  const minValue = e.target.value ? parseFloat(e.target.value) : null;
                  setStateValue((cur) => ({ min: minValue ?? null, max: cur?.max ?? null }));

                  if (stateValue?.max && minValue && Number(stateValue.max) < minValue) {
                    setStateValue({ min: minValue, max: null });
                    onMinChange(minValue, null);
                    return;
                  } else {
                    onMinChange(minValue, stateValue?.max ? Number(stateValue.max) : null);
                  }
                }}
                disabled={disabled}
                fullWidth
              />
            </Grid>
          </Grid>
        </FormControl>
      </Grid>
      <Grid>
        <Typography variant='caption'>To</Typography>
      </Grid>

      <Grid sx={{ flex: 2 }}>
        <FormControl>
          <Grid
            container
            sx={{
              flexDirection: 'row',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Grid sx={{ width: 'auto' }}>
              <TextField
                type='number'
                inputMode='numeric'
                slotProps={{
                  htmlInput: {
                    min: 20,
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment
                        position='start'
                        sx={(theme) => ({
                          height: 0,
                          svg: {
                            ...theme.icons.xs,
                            ml: '-12px',
                          },
                        })}
                      >
                        <DollarSign />
                      </InputAdornment>
                    ),
                  },
                }}
                value={stateValue ? stateValue.max : null}
                onChange={(e) => {
                  const maxValue = e.target.value ? parseFloat(e.target.value) : null;
                  setStateValue((cur) => ({ min: cur?.min ?? null, max: maxValue }));
                  onMaxChange(maxValue, stateValue?.min ? Number(stateValue.min) : null);

                  // future consideration: some sort of debounce or adjustment if min > max
                  // (right now left out the second you type this applies). Shelby said it's okay for now and its on the user
                }}
                disabled={disabled}
                fullWidth
              />
            </Grid>
          </Grid>
        </FormControl>
      </Grid>
    </Grid>
  );
};
